/**
 * wireMinimapPingClick — Canvas click → world ping emit stub (SPY-414 / SPY-514).
 */

import { minimapPxToWorld } from '../../domain/map/minimapProjection.js';

export const MINIMAP_PING_TYPE_CYCLE = Object.freeze([
  'danger', 'on_my_way', 'assist', 'missing'
]);

/**
 * @param {{
 *   canvasEl?: { addEventListener?: Function, getBoundingClientRect?: Function, width?: number, height?: number },
 *   getBounds?: () => { minX: number, maxX: number, minZ: number, maxZ: number }|null,
 *   getAngleDeg?: () => number,
 *   emitPing?: (type: string, x: number, z: number) => unknown,
 *   defaultType?: string,
 *   onCycleIndexChange?: (index: number, type: string) => void,
 *   globalObj?: object|null
 * }} [opts]
 */
export function wireMinimapPingClick({
  canvasEl = null,
  getBounds = null,
  getAngleDeg = () => 0,
  emitPing = null,
  defaultType = 'danger',
  initialCycleIndex = 0,
  onCycleIndexChange = null,
  globalObj = typeof window !== 'undefined' ? window : null
} = {}) {
  const startIdx = MINIMAP_PING_TYPE_CYCLE.includes(defaultType)
    ? MINIMAP_PING_TYPE_CYCLE.indexOf(defaultType)
    : 0;
  let cycleIndex = Number.isInteger(initialCycleIndex) && initialCycleIndex >= 0
    ? initialCycleIndex % MINIMAP_PING_TYPE_CYCLE.length
    : startIdx;
  let pingType = MINIMAP_PING_TYPE_CYCLE[cycleIndex];

  function resolveEmit() {
    if (typeof emitPing === 'function') return emitPing;
    const hook = globalObj?.__debugPing;
    if (hook && typeof hook.emit === 'function') {
      return (type, x, z) => hook.emit(type, x, z);
    }
    return null;
  }

  function getPingType() {
    return pingType;
  }

  function cyclePingType() {
    cycleIndex = (cycleIndex + 1) % MINIMAP_PING_TYPE_CYCLE.length;
    pingType = MINIMAP_PING_TYPE_CYCLE[cycleIndex];
    if (typeof onCycleIndexChange === 'function') {
      onCycleIndexChange(cycleIndex, pingType);
    }
    return pingType;
  }

  function getPingCycleIndex() {
    return cycleIndex;
  }

  function setPingCycleIndex(n) {
    if (!Number.isInteger(n)) return cycleIndex;
    cycleIndex = Math.min(3, Math.max(0, n));
    pingType = MINIMAP_PING_TYPE_CYCLE[cycleIndex];
    if (typeof onCycleIndexChange === 'function') {
      onCycleIndexChange(cycleIndex, pingType);
    }
    return cycleIndex;
  }

  function getPingCycleSnapshot() {
    return {
      index: cycleIndex,
      type: pingType,
      cycleLength: MINIMAP_PING_TYPE_CYCLE.length,
      types: [...MINIMAP_PING_TYPE_CYCLE]
    };
  }

  function handleClick(event) {
    if (!canvasEl || typeof getBounds !== 'function') return null;
    const bounds = getBounds();
    if (!bounds) return null;
    const emit = resolveEmit();
    if (!emit) return null;

    const rect = typeof canvasEl.getBoundingClientRect === 'function'
      ? canvasEl.getBoundingClientRect()
      : { left: 0, top: 0, width: canvasEl.width || 1, height: canvasEl.height || 1 };
    const scaleX = (canvasEl.width || rect.width || 1) / (rect.width || 1);
    const scaleY = (canvasEl.height || rect.height || 1) / (rect.height || 1);
    const px = (event.clientX - rect.left) * scaleX;
    const py = (event.clientY - rect.top) * scaleY;
    const world = minimapPxToWorld(
      px,
      py,
      bounds,
      canvasEl.width || rect.width || 1,
      canvasEl.height || rect.height || 1,
      { angleDeg: typeof getAngleDeg === 'function' ? getAngleDeg() : 0 }
    );
    const type = pingType;
    const result = emit(type, world.x, world.z);
    cyclePingType();
    return result;
  }

  function handleContextMenu(event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    return cyclePingType();
  }

  if (canvasEl && typeof canvasEl.addEventListener === 'function') {
    canvasEl.addEventListener('click', handleClick);
    canvasEl.addEventListener('contextmenu', handleContextMenu);
  }

  return {
    handleClick,
    handleContextMenu,
    getPingType,
    cyclePingType,
    getPingCycleIndex,
    setPingCycleIndex,
    getPingCycleSnapshot
  };
}
